import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Link, Navigate, Outlet, useParams } from "react-router";
import { SettingsSidebar } from "@/components/settings/settings-sidebar";
import { useMe } from "@/hooks/use-me";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useWorkspaceStore } from "@/store/workspace-store";

export function OrgSettingsLayout() {
  const { orgId: urlOrgId } = useParams<{ orgId: string }>();
  const { isLoading } = useMe();
  const { orgId, isOrgAdmin } = useActiveOrg();
  const { activeWorkspaceId, isInitialized, isInitializing, initializeWorkspaces } =
    useWorkspaceStore();

  // This route is reached directly on a hard browser navigation (e.g. Stripe
  // redirecting back from checkout), not just via in-app links from
  // WorkspaceRouteWrapper — so the workspace store may not have been
  // initialized yet in this page load. Kick it off ourselves rather than
  // relying on another route to have done it first.
  useEffect(() => {
    if (!isInitialized && !isInitializing) {
      initializeWorkspaces().catch((error) => {
        console.error("Failed to initialize workspaces:", error);
      });
    }
  }, [isInitialized, isInitializing, initializeWorkspaces]);

  // Wait for both /me and the workspace store before deciding. activeWorkspace
  // (role + affiliated_company) drives every guard below; redirecting before
  // it hydrates can bounce a real admin to /workspaces on hard refresh.
  if (isLoading || !isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No resolvable org: bounce to the workspaces picker. The org-settings
  // surface is only meaningful for users tied to a workspace/org.
  if (!orgId) {
    return <Navigate to="/workspaces" replace />;
  }

  // Non-admins (workspace members) get 403'd by ensureOrgManagerOwnership on
  // every billing endpoint, leaving an empty page with red network calls.
  // Bounce them back to the app so direct URL hits don't dead-end.
  if (!isOrgAdmin) {
    return (
      <Navigate
        to={activeWorkspaceId ? `/${activeWorkspaceId}/dashboard` : "/workspaces"}
        replace
      />
    );
  }

  // The BE already 403s mismatched orgs via ensureOrgManagerOwnership, but
  // the API errors would land before this guard, flashing empty UI. Redirect
  // any URL that names a different org to the active workspace's org so deep
  // links are forgiving (e.g. shared screenshots, copy-pasted IDs).
  if (urlOrgId && urlOrgId !== orgId) {
    return <Navigate to={`/org/${orgId}/settings/billing`} replace />;
  }

  return (
    <div
      className="flex h-dvh flex-col overflow-hidden md:flex-row"
      style={
        {
          "--sidebar-background": "0 0% 98.4%",
          "--sidebar-foreground": "0 0% 9%",
          "--sidebar-border": "0 0% 89.8%",
          "--sidebar-primary": "0 0% 9%",
          "--sidebar-primary-foreground": "0 0% 98%",
          "--sidebar-accent": "0 0% 95%",
          "--sidebar-accent-foreground": "0 0% 9%",
        } as React.CSSProperties
      }
    >
      {/* Desktop sidebar — hidden on mobile (single section, no menu needed) */}
      <div className="hidden md:flex md:h-full">
        <SettingsSidebar />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Mobile back-arrow to return to the app — no settings menu here
            since org-settings only has one page (billing). */}
        <header className="md:hidden sticky top-0 z-20 flex h-12 shrink-0 items-center border-b border-border bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <Link
            to={
              activeWorkspaceId
                ? `/${activeWorkspaceId}/dashboard`
                : "/workspaces"
            }
            className="flex h-9 items-center gap-1.5 rounded px-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to app
          </Link>
        </header>
        <div className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
